import type { PanoramaHotspot, PanoramaPosition, ViewerEntityId } from '../contracts/models';

export interface PannellumHotspotConfig {
  id: string;
  pitch: number;
  yaw: number;
  type: 'info';
  cssClass?: string;
  createTooltipFunc?: (element: HTMLElement) => void;
  createTooltipArgs?: Readonly<Record<string, unknown>>;
  clickHandlerFunc?: () => void;
}

export interface PannellumViewerConfig {
  type: 'equirectangular';
  panorama: string;
  autoLoad: boolean;
  showControls: boolean;
  showZoomCtrl: boolean;
  showFullscreenCtrl: boolean;
  hotSpotDebug: boolean;
  pitch: number;
  yaw: number;
  hfov: number;
  hotSpots: readonly PannellumHotspotConfig[];
}

export interface PannellumViewer {
  addHotSpot(config: PannellumHotspotConfig): void;
  removeHotSpot(id: string): void;
  destroy(): void;
  resize(): void;
  lookAt(pitch: number, yaw: number, hfov?: number, duration?: number): void;
  on(event: 'load', handler: () => void): void;
  on(event: 'error', handler: (error: unknown) => void): void;
  getPitch(): number;
  getYaw(): number;
  getHfov(): number;
  isOrientationActive(): boolean;
  isOrientationSupported(): boolean;
  startOrientation(): void;
}

export interface PannellumFactory {
  viewer(
    container: string | HTMLElement,
    config: PannellumViewerConfig,
  ): PannellumViewer;
}

export interface PanoramaTarget extends Pick<PanoramaPosition, 'pitch' | 'yaw'> {
  duration?: number;
}

export interface PanoramaHotspotElementContext {
  element: HTMLElement;
  hotspot: PanoramaHotspot;
  domId: string;
}

export type PanoramaHotspotElementRenderer = (
  context: PanoramaHotspotElementContext,
) => void;

export const panoramaHotspotDomId = (id: ViewerEntityId): string =>
  `hotspot-${String(id)}`;

export const defaultPanoramaHotspotElementRenderer: PanoramaHotspotElementRenderer = ({
  element,
  domId,
}) => {
  element.setAttribute('data-hs-id', domId);
  element.setAttribute('aria-label', 'Panorama hotspot');
};

export interface BuildPannellumHotspotConfigOptions {
  className?: string;
  renderElement?: PanoramaHotspotElementRenderer;
  onClick?: (hotspot: PanoramaHotspot) => void;
}

export const buildPannellumHotspotConfig = (
  hotspot: PanoramaHotspot,
  options: BuildPannellumHotspotConfigOptions = {},
): PannellumHotspotConfig => {
  const domId = panoramaHotspotDomId(hotspot.id);
  const renderElement =
    options.renderElement ?? defaultPanoramaHotspotElementRenderer;

  return {
    id: domId,
    pitch: hotspot.pitch,
    yaw: hotspot.yaw,
    type: 'info',
    cssClass: options.className ?? 'iv-panorama-hotspot',
    createTooltipFunc: (element) =>
      renderElement({ element, hotspot, domId }),
    createTooltipArgs: {},
    clickHandlerFunc: () => options.onClick?.(hotspot),
  };
};

export interface SyncPannellumHotspotsOptions
  extends BuildPannellumHotspotConfigOptions {
  previousIds?: ReadonlySet<string>;
}

export interface SyncPannellumHotspotsResult {
  ids: ReadonlySet<string>;
  added: number;
}

export const syncPannellumHotspots = (
  viewer: PannellumViewer,
  hotspots: readonly PanoramaHotspot[],
  options: SyncPannellumHotspotsOptions = {},
): SyncPannellumHotspotsResult => {
  const nextIds = new Set(hotspots.map((hotspot) => panoramaHotspotDomId(hotspot.id)));
  const idsToRemove = new Set([...(options.previousIds ?? []), ...nextIds]);

  idsToRemove.forEach((id) => {
    try {
      viewer.removeHotSpot(id);
    } catch {
      // Pannellum throws when a hotspot is not currently mounted.
    }
  });

  let added = 0;
  hotspots.forEach((hotspot) => {
    try {
      viewer.addHotSpot(buildPannellumHotspotConfig(hotspot, options));
      added += 1;
    } catch {
      // A later watchdog pass will retry failed additions.
    }
  });

  return { ids: nextIds, added };
};

export const panoramaPositionWithPitchOffset = (
  target: PanoramaTarget,
  pitchOffset = 0,
): PanoramaTarget => ({
  ...target,
  pitch: target.pitch + pitchOffset,
});
